import React, { Fragment, Suspense, lazy } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import type { ColourFinish } from '../types';
import { PUBLIC_VIEWER_ENGINE } from '../lib/productViewerEngine';

const ModelViewer = lazy(() => import('./ModelViewer'));
const VaseStage3D = lazy(() =>
  import('./vaseStage3d').then((module) => ({ default: module.VaseStage3D }))
);

interface ModelPreviewModalProps {
  open: boolean;
  onClose: () => void;
  modelPath?: string | null;
  vaseId?: number;
  selectedColors?: string[];
  finishType?: string;
  title?: string;
}

const ModelPreviewModal: React.FC<ModelPreviewModalProps> = ({
  open,
  onClose,
  modelPath,
  vaseId,
  selectedColors = [],
  finishType,
  title = '3D model preview',
}) => {
  const canRenderViewer = open && typeof modelPath === 'string' && modelPath.trim().length > 0;
  const resolvedFinish = (finishType ?? 'shifting') as ColourFinish;

  const viewerFallback = (
    <div className="flex h-screen items-center justify-center text-white">
      <div className="text-center">
        <div className="mb-4 inline-block h-16 w-16 animate-spin rounded-full border-b-4 border-white" />
        <p className="text-lg">Loading 3D preview...</p>
      </div>
    </div>
  );

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[320]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative h-screen w-full">
                <button
                  type="button"
                  onClick={onClose}
                  className="absolute right-4 top-4 z-[330] rounded-full bg-white p-3 text-black shadow-lg transition-colors hover:bg-stone-200"
                  aria-label="Close 3D preview"
                >
                  <X className="h-5 w-5" />
                </button>

                <Dialog.Title className="sr-only">{title}</Dialog.Title>
                <Dialog.Description className="sr-only">
                  Interactive 3D preview. Use your mouse or touch to rotate the model. Press Escape or use the close button to exit.
                </Dialog.Description>

                {canRenderViewer ? (
                  <Suspense fallback={viewerFallback}>
                    {PUBLIC_VIEWER_ENGINE === 'vaseStage3d' ? (
                      <VaseStage3D
                        key={modelPath}
                        profile="product"
                        model={{
                          modelPath: modelPath ?? '',
                          vaseId,
                          selectedColors,
                          finishType: resolvedFinish,
                        }}
                        interaction={{
                          enableDrag: true,
                          enableZoom: false,
                        }}
                        container={{
                          className: 'h-screen w-full',
                          backgroundColor: '#e8e4dc',
                        }}
                      />
                    ) : (
                      <ModelViewer
                        modelPath={modelPath ?? undefined}
                        vaseId={vaseId}
                        selectedColors={selectedColors}
                        finishType={finishType}
                        isPreview
                        publicLightingMode="product"
                      />
                    )}
                  </Suspense>
                ) : (
                  <div className="flex h-screen items-center justify-center text-white">
                    <p className="text-lg">Model preview unavailable</p>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ModelPreviewModal;
